import React, { useState, useEffect } from 'react';
import { Col, Row, Table, Button } from 'reactstrap';
import AuthRequired from '../components/AuthRequired';
import { getDxReports } from '../queries';
import * as XLSX from 'xlsx';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import Select from 'react-select';
import Pagination from '../components/Pagination';
import { displayError } from '../utilities';

const itemsPerPage = 10;

const DxApp = () => {
  const specialities = [
    { label: 'All', value: 0 },
    { label: 'Pediatrics', value: 1 },
    { label: 'Obstetrics & Gynecology', value: 2 },
    { label: 'Fertility', value: 3 }
  ];

  const [fromDate, setFromDate] = useState(
    moment()
      .startOf('day')
      .toDate()
  );
  const [toDate, setToDate] = useState(
    moment()
      .endOf('day')
      .toDate()
  );
  const [reports, setReports] = useState([]);
  const [selectedSpeciality, setSelectedSpeciality] = useState(specialities[0]);
  const [currentPage, setCurrentPage] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [adoptionPercentage, setAdoptionPercentage] = useState(0);
  const [loading, setLoading] = useState(false);

  const getRangeError = (start, end) => {
    if (!start || !end) return 'Please select both From and To dates.';
    if (moment(start).isAfter(moment(end))) {
      return 'From Date cannot be after To Date.';
    }
    if (moment(end).diff(moment(start), 'days') >= 7) {
      return 'Date range cannot exceed 7 days.';
    }
    return '';
  };

  const handleFromDateChange = date => {
    const newFrom = date
      ? moment(date)
          .startOf('day')
          .toDate()
      : null;
    const newTo =
      newFrom && toDate && moment(newFrom).isAfter(toDate)
        ? moment(newFrom)
            .endOf('day')
            .toDate()
        : toDate;

    setFromDate(newFrom);
    setToDate(newTo);
    setErrorMessage(getRangeError(newFrom, newTo));
  };

  const handleToDateChange = date => {
    const newTo = date
      ? moment(date)
          .endOf('day')
          .toDate()
      : null;
    const newFrom =
      newTo && fromDate && moment(newTo).isBefore(fromDate)
        ? moment(newTo)
            .startOf('day')
            .toDate()
        : fromDate;

    setFromDate(newFrom);
    setToDate(newTo);
    setErrorMessage(getRangeError(newFrom, newTo));
  };

  useEffect(() => {
    fetchReports(fromDate, toDate);
    // eslint-disable-next-line
  }, [selectedSpeciality]);

  const fetchReports = (fromDate, toDate) => {
    const error = getRangeError(fromDate, toDate);
    if (error) {
      setErrorMessage(error);
      return;
    }

    setErrorMessage('');
    setLoading(true);
    const hospitalId = parseInt(localStorage.getItem('DEFAULT_HOSPITAL_ID'));
    const startDate = moment(fromDate)
      .startOf('day')
      .toDate();
    const endDate = moment(toDate)
      .endOf('day')
      .toDate();
    const departmentId = selectedSpeciality.value || null;

    getDxReports(hospitalId, startDate, endDate, departmentId)
      .then(data => {
        if (data.data && data.data.getDxReports) {
          setReports(data.data.getDxReports.data);
          setAdoptionPercentage(data.data.getDxReports.adoptionPercentage);
          setErrorMessage('');
        } else {
          setReports([]);
          setAdoptionPercentage(0);
          setErrorMessage('');
        }
      })
      .catch(error => {
        displayError('Something went wrong', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const downloadExcel = () => {
    const columnOrder = [
      'Date',
      "Patient's Name",
      'Contact Number',
      'MPID',
      "Consultant's Name",
      'Lab Test',
      'Scan',
      'Medicines',
      'Vaccines',
      'Procedures',
      'Notes',
      'Follow up date',
      'Prescription Link'
    ];

    const data = reports.map(report => ({
      Date: moment(report.booking.slotTime).format('MM/DD/YYYY'),
      "Patient's Name": report.booking.userName,
      'Contact Number': report.booking.userMobile,
      MPID: report.booking.mpid,
      "Consultant's Name": report.booking.objectName,
      'Lab Test': report.labs
        ? report.labs.map(lab => lab.name).join(', ')
        : '',
      Scan: report.scans ? report.scans.map(scan => scan.name).join(', ') : '',
      Medicines: report.medicines
        ? report.medicines.map(medicine => medicine.name).join(', ')
        : '',
      Vaccines: report.administeredVaccines
        ? report.administeredVaccines
            .map(vaccine => vaccine.vaccine.name)
            .join(', ')
        : '',
      Procedures: report.procedures
        ? report.procedures
            .map(proc => `${proc.name} ${proc.notes ? ` (${proc.notes})` : ''}`)
            .join(', ')
        : '',
      Notes: report.notes ? report.notes.note.replace(/\\n\n?/g, '\n') : '',
      'Follow up date': report.followupDate || '',
      'Prescription Link': report.prescriptionLink || ''
    }));

    const worksheet = XLSX.utils.json_to_sheet(data, { header: columnOrder });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Reports');
    XLSX.writeFile(workbook, 'dxreports.xlsx');
  };

  return (
    <AuthRequired pageName="dxapp">
      <div style={{ margin: '1rem' }}>
        <Row>
          <Col>
            <h1>DxApp Dashboard</h1>
          </Col>
        </Row>
        <Row className="toolbar">
          <Col>
            <label className="bold">Speciality:</label>
            <Select
              value={selectedSpeciality}
              placeholder="Select speciality"
              options={specialities}
              onChange={v => setSelectedSpeciality(v)}
              getOptionLabel={option => option.label}
              getOptionValue={option => option.value}
            />
          </Col>
          <Col className="adoption-percentage-container">
            <label className="adoption-percentage">Adoption Percentage:</label>
            <div style={{ marginLeft: '5px', position: 'relative' }}>
              <div className="battery-container">
                <div
                  className="battery-fill"
                  style={{ height: `${adoptionPercentage}%` }}
                ></div>
              </div>
              <div>{adoptionPercentage}%</div>
            </div>
          </Col>
        </Row>
        <Row className="toolbar">
          <Col sm={3}>
            <label className="bold">From Date:</label>
            <DatePicker
              selected={fromDate ? moment(fromDate) : null}
              maxDate={moment()}
              dateFormat="DD MMM YYYY"
              selectsStart
              startDate={fromDate ? moment(fromDate) : null}
              endDate={toDate ? moment(toDate) : null}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              onChange={handleFromDateChange}
              className="reports-date-height"
            />
          </Col>
          <Col sm={3}>
            <label className="bold">
              To Date:{' '}
              <span
                className="text-muted"
                style={{ fontWeight: 'normal', fontSize: '12px' }}
              >
                (max &lt; 7 days)
              </span>
            </label>
            <div style={{ position: 'relative' }}>
              <DatePicker
                selected={toDate ? moment(toDate) : null}
                maxDate={moment()}
                dateFormat="DD MMM YYYY"
                selectsEnd
                startDate={fromDate ? moment(fromDate) : null}
                endDate={toDate ? moment(toDate) : null}
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                onChange={handleToDateChange}
                className="reports-date-height"
              />
              {errorMessage && (
                <div className="error-message">{errorMessage}</div>
              )}
            </div>
          </Col>
          <Col>
            <Button
              style={{ marginTop: '1.8rem' }}
              color="primary"
              onClick={() => fetchReports(fromDate, toDate)}
              disabled={
                loading || Boolean(errorMessage) || !fromDate || !toDate
              }
            >
              {loading ? 'Loading...' : 'Get Reports'}
            </Button>
          </Col>
        </Row>
        <Row className="toolbar">
          <Col style={{ marginTop: '1rem' }}>
            <Table size="sm" striped responsive>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Patient&apos;s Name</th>
                  <th>Contact Number</th>
                  <th>MPID</th>
                  <th>Consultant&apos;s Name</th>
                  <th>Lab Test</th>
                  <th>Scan</th>
                  <th>Medicines</th>
                  <th>Vaccines</th>
                  <th>Procedures</th>
                  <th>Notes</th>
                  <th>Follow up date</th>
                  <th>Prescription Link</th>
                </tr>
              </thead>
              <tbody>
                {reports.length === 0 ? (
                  <tr>
                    <td colSpan="20" className="text-center bold">
                      No data available
                    </td>
                  </tr>
                ) : (
                  reports.slice(startIndex, endIndex).map((report, index) => (
                    <tr key={startIndex + index}>
                      <td>{startIndex + index + 1}</td>
                      <td>
                        {moment(report.booking.slotTime).format('MM/DD/YYYY')}
                      </td>
                      <td>{report.booking.userName}</td>
                      <td>{report.booking.userMobile}</td>
                      <td>{report.booking.mpid}</td>
                      <td>{report.booking.objectName}</td>
                      <td>
                        {report.labs &&
                          report.labs.map((lab, labIndex) => (
                            <div key={labIndex}>{lab.name}</div>
                          ))}
                      </td>
                      <td>
                        {report.scans &&
                          report.scans.map((scan, scanIndex) => (
                            <div key={scanIndex}>{scan.name}</div>
                          ))}
                      </td>
                      <td>
                        {report.medicines &&
                          report.medicines.map((medicine, medicineIndex) => (
                            <div key={medicineIndex}>{medicine.name}</div>
                          ))}
                      </td>
                      <td>
                        {report.administeredVaccines &&
                          report.administeredVaccines.map(
                            (vaccine, vaccineIndex) => (
                              <div key={vaccineIndex}>
                                {vaccine.vaccine.name}
                              </div>
                            )
                          )}
                      </td>
                      <td>
                        {report.procedures &&
                          report.procedures.map((proc, procIndex) => (
                            <div key={procIndex}>
                              {proc.name}
                              {proc.notes ? ` (${proc.notes})` : ''}
                            </div>
                          ))}
                      </td>
                      <td style={{ whiteSpace: 'pre-line' }}>
                        {report.notes && report.notes.note
                          ? report.notes.note.replace(/\\n\n?/g, '\n')
                          : ''}
                      </td>
                      <td>{report.followupDate}</td>
                      <td>
                        {report.prescriptionLink && (
                          <a
                            href={report.prescriptionLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open
                          </a>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
        {reports.length > 0 && (
          <Row className="toolbar">
            <Col className="pagination-container">
              <Pagination
                currentPage={currentPage}
                setCurrentPage={handlePageChange}
                totalPages={Math.ceil(reports.length / itemsPerPage)}
              />
            </Col>
            <Col xs="auto" className="download-button">
              <Button color="success" onClick={downloadExcel}>
                Download Report
              </Button>
            </Col>
          </Row>
        )}
      </div>
    </AuthRequired>
  );
};

export default DxApp;
